<template>
  <b-card>
    <b-alert
      variant="primary"
      show
      class="mb-2"
    >
      <div class="alert-body">
        {{ $t('กำหนดค่า') }} <b>{{ $t('ถือสู้/แบ่งหุ้น') }}</b> {{ $t('ของเอเย่นต์สายล่าง 1 ชั้น (รายตลาดหวย) — หากไม่ตั้งค่ารายหวย ระบบจะใช้ค่าทุกหวยของเอเย่นต์อัตโนมัติ') }}
      </div>
    </b-alert>

    <!-- เลือกหวย -->
    <b-card class="border mb-2">
      <div class="d-flex align-items-center mb-2">
        <feather-icon
          icon="GridIcon"
          size="18"
          class="mr-50 text-primary"
        />
        <h5 class="mb-0">
          {{ $t('เลือกหวย') }}
        </h5>
      </div>
      <b-row class="align-items-end">
        <b-col
          cols="12"
          md="5"
          lg="4"
        >
          <label class="small mb-50">
            {{ $t('ตลาดหวย') }}
            <span class="text-muted">{{ $t('(ไม่เลือก = ทุกหวย)') }}</span>
          </label>
          <v-select
            v-model="selectedHuay"
            :options="$tOptions(huayOptions)"
            :disabled="isLoadingHuays"
            :loading="isLoadingHuays"
            label="name"
            :placeholder="isLoadingHuays ? $t('กำลังโหลด...') : $t('ทุกหวย')"
            @input="onHuayChange"
          >
            <template #option="option">
              <div class="d-flex align-items-center">
                <span
                  v-if="getCountryCode(option.name)"
                  class="flag-icon flag-icon-rounded mr-1"
                  :class="`flag-icon-${getCountryCode(option.name)}`"
                />
                <span>{{ option.name }}</span>
              </div>
            </template>
            <template #selected-option="option">
              <div class="d-flex align-items-center">
                <span
                  v-if="getCountryCode(option.name)"
                  class="flag-icon flag-icon-rounded mr-1"
                  :class="`flag-icon-${getCountryCode(option.name)}`"
                />
                <span>{{ option.name }}</span>
              </div>
            </template>
          </v-select>
        </b-col>
        <b-col
          cols="12"
          md="auto"
          class="mt-1 mt-md-0"
        >
          <b-button
            variant="info"
            :disabled="!selectedHuay || isLoadingDownline"
            @click="openCopyModal"
          >
            <feather-icon
              icon="CopyIcon"
              class="mr-50"
            />
            {{ $t('คัดลอกไปหวยอื่น') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- จัดการเอเย่นต์ -->
    <b-card class="border mb-0">
      <div class="d-flex align-items-center mb-2">
        <feather-icon
          icon="UsersIcon"
          size="18"
          class="mr-50 text-primary"
        />
        <h5 class="mb-0">
          {{ $t('จัดการเอเย่นต์') }}
        </h5>
      </div>
      <b-row class="mb-2">
        <b-col
          cols="12"
          md="4"
        >
          <label class="small mb-50">{{ $t('ค้นหา') }}</label>
          <b-form-input
            v-model="filterText"
            :placeholder="$t('ชื่อผู้ใช้')"
            debounce="200"
          />
        </b-col>
        <b-col class="text-right align-self-end mt-1 mt-md-0">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1"
            :disabled="!hasPendingChanges"
            @click="resetAllPending"
          >
            <feather-icon
              icon="RotateCcwIcon"
              class="mr-50"
            />
            {{ $t('กลับค่าเดิม') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="!hasPendingChanges || isSaving"
            @click="savePendingChanges"
          >
            <b-spinner
              v-if="isSaving"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              class="mr-50"
            />
            {{ $t('บันทึก') }} ({{ pendingCount }})
          </b-button>
        </b-col>
      </b-row>

      <b-alert
        variant="danger"
        show
        class="mb-2"
      >
        <div class="alert-body small">
          {{ $t('ให้เปอร์เซนต์ได้ไม่ต่ำกว่าที่เอเย่นต์แบ่งให้ลูกสายไปแล้ว และไม่เกินเปอร์เซนต์ของเรา') }}
        </div>
      </b-alert>

      <div class="table-responsive">
        <table class="table table-bordered table-sm table-hover percent-table">
          <thead class="thead-light">
            <tr>
              <th style="min-width: 40px">
                #
              </th>
              <th style="min-width: 120px">
                {{ $t('ชื่อผู้ใช้') }}
              </th>
              <th style="min-width: 120px">
                {{ $t('ชื่อลูกค้า') }}
              </th>
              <th
                class="text-center"
                style="min-width: 180px"
              >
                {{ $t('accountMembers.fields.givenPt') }}
              </th>
              <th
                class="text-center"
                style="min-width: 160px"
              >
                {{ $t('accountMembers.fields.ourPt') }}
              </th>
              <th
                class="text-center"
                style="min-width: 140px"
              >
                {{ $t('เอเย่นต์ให้ลูก') }}
              </th>
              <th
                class="text-center"
                style="min-width: 100px"
              >
                {{ $t('รูปแบบ') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="!isLoadingDownline">
              <tr
                v-for="(row, index) in filteredRows"
                :key="row.username"
                :class="{
                  'row-dirty': isRowDirty(row),
                  'row-invalid': !isRowValid(row),
                }"
              >
                <td class="align-middle text-center">
                  {{ index + 1 }}
                </td>
                <td class="align-middle">
                  {{ row.username }}
                </td>
                <td class="align-middle">
                  {{ row.nickName || '-' }}
                </td>
                <td class="align-middle p-1">
                  <div class="d-flex align-items-center">
                    <b-form-input
                      v-model.number="row.editPercent"
                      type="number"
                      size="sm"
                      :min="row.minPercent"
                      :max="callerPercent"
                      step="0.01"
                      class="text-right"
                      :class="{ 'is-invalid': !isRowValid(row) }"
                    />
                    <span class="ml-1 small text-muted">%</span>
                  </div>
                  <small
                    v-if="isBlank(row)"
                    class="text-danger"
                  >
                    {{ $t('กรอกเปอร์เซนต์') }}
                  </small>
                  <small
                    v-else-if="isBelowMin(row)"
                    class="text-danger"
                  >
                    {{ $t('ต้องไม่ต่ำกว่า') }} {{ formatPercent(row.minPercent) }}
                  </small>
                  <small
                    v-else-if="!isRowValid(row)"
                    class="text-danger"
                  >
                    {{ $t('ให้ได้สูงสุด') }} {{ formatPercent(callerPercent) }}
                  </small>
                </td>
                <td class="align-middle text-center font-weight-bold">
                  {{ formatPercent(ourPercent(row)) }}
                </td>
                <td class="align-middle text-center text-muted">
                  {{ formatPercent(row.minPercent) }}
                </td>
                <td class="align-middle text-center">
                  <b-badge
                    v-if="row.isCustom"
                    variant="light-warning"
                    class="mr-1"
                  >
                    {{ $t('รายหวย') }}
                  </b-badge>
                  <b-badge
                    v-else
                    variant="light-secondary"
                    class="mr-1"
                  >
                    {{ $t('ทุกหวย') }}
                  </b-badge>
                  <b-button
                    v-if="row.isCustom"
                    variant="flat-warning"
                    size="sm"
                    class="btn-icon mr-1"
                    :title="$t('คัดลอกไปหวยอื่น')"
                    @click="openCopyModalForRow(row)"
                  >
                    <feather-icon icon="CopyIcon" />
                  </b-button>
                  <b-button
                    v-if="row.isCustom"
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon"
                    :title="$t('รีเซ็ตกลับค่าทุกหวย')"
                    @click="resetToGlobal(row)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </td>
              </tr>
              <tr v-if="filteredRows.length === 0">
                <td
                  colspan="7"
                  class="text-center text-muted py-3"
                >
                  {{ $t('ไม่มีข้อมูลเอเย่นต์สายล่าง') }}
                </td>
              </tr>
            </template>
            <tr v-else-if="isLoadingDownline">
              <td
                colspan="7"
                class="text-center text-muted py-3"
              >
                <b-spinner
                  small
                  variant="primary"
                  class="mr-1"
                />
                {{ $t('กำลังโหลด...') }}
              </td>
            </tr>
            <tr v-else>
              <td
                colspan="7"
                class="text-center text-muted py-3"
              >
                -
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <!-- Modal: คัดลอกไปหวยอื่น -->
    <b-modal
      v-model="showCopyModal"
      :title="$t('คัดลอกไปตลาดหวยอื่น')"
      size="lg"
      :ok-title="$t('คัดลอก')"
      :cancel-title="$t('ยกเลิก')"
      :ok-disabled="copyTargetIds.length === 0 || !copyContext"
      @ok.prevent="submitCopy"
    >
      <div v-if="copyContext">
        <p class="mb-2">
          {{ $t('คัดลอกค่าของ') }} <b>{{ copyContext.username }}</b>
          {{ $t('จากตลาด') }} <b>{{ copyContext.sourceName }}</b>
          {{ $t('ไปยังตลาดหวยที่เลือก') }} ({{ $t('ค่าที่คัดลอกคือ') }} "{{ $t('accountMembers.fields.givenPt') }}" = {{ formatPercent(copyContext.percent) }})
        </p>
        <b-form-input
          v-model="copyFilter"
          :placeholder="$t('ค้นหาตลาดหวย...')"
          size="sm"
          class="mb-2"
          debounce="150"
        />
        <div class="copy-huay-list">
          <b-form-checkbox
            v-for="opt in filteredCopyOptions"
            :key="opt.id"
            :checked="copyTargetIds.includes(opt.id)"
            @change="toggleCopyTarget(opt.id, $event)"
          >
            <span
              v-if="getCountryCode(opt.name)"
              class="flag-icon flag-icon-rounded mr-1"
              :class="`flag-icon-${getCountryCode(opt.name)}`"
            />
            {{ opt.name }}
          </b-form-checkbox>
        </div>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BAlert, BSpinner,
  BBadge, BModal, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import agentService from '@/service/agentService'
import lottoService from '@/service/lottoService'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { countryFlagMixin } from '@core/mixins/ui/countryFlag'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BAlert,
    BSpinner,
    BBadge,
    BModal,
    BFormCheckbox,
    vSelect,
  },
  mixins: [countryFlagMixin],
  data() {
    return {
      isLoadingHuays: true,
      isLoadingDownline: false,
      isSaving: false,
      huayOptions: [],
      selectedHuay: null,
      callerPercent: 0,
      callerIsCustom: false,
      rows: [],
      filterText: '',
      showCopyModal: false,
      copyContext: null,
      copyTargetIds: [],
      copyFilter: '',
    }
  },
  computed: {
    currentUsername() {
      return this.$store.state.app.userData?.username || ''
    },
    selectedHuayId() {
      return this.selectedHuay ? this.selectedHuay.id : null
    },
    filteredRows() {
      const f = this.filterText.trim().toLowerCase()
      if (!f) return this.rows
      return this.rows.filter(r => (r.username || '').toLowerCase().includes(f)
        || (r.nickName || '').toLowerCase().includes(f))
    },
    dirtyRows() {
      return this.rows.filter(r => this.isRowDirty(r))
    },
    pendingCount() {
      return this.dirtyRows.length
    },
    // ตรวจเฉพาะแถวที่แก้ — แถวที่ค่าเดิมหลุดช่วงอยู่แล้ว (ลูกถือมากกว่าพ่อ) ต้องไม่ล็อกการบันทึกแถวอื่น
    hasPendingChanges() {
      return this.dirtyRows.length > 0 && this.dirtyRows.every(r => this.isRowValid(r))
    },
    filteredCopyOptions() {
      const list = this.huayOptions.filter(h => h.id !== this.selectedHuayId)
      const f = this.copyFilter.trim().toLowerCase()
      if (!f) return list
      return list.filter(h => (h.name || '').toLowerCase().includes(f))
    },
  },
  async mounted() {
    await this.loadHuays()
  },
  methods: {
    toastSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t('สำเร็จ'), icon: 'CheckCircleIcon', variant: 'success', text,
        },
      })
    },
    toastError(text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t('เกิดข้อผิดพลาด'), icon: 'AlertCircleIcon', variant: 'danger', text,
        },
      })
    },
    formatPercent(n) {
      if (n === null || n === undefined || Number.isNaN(Number(n))) return '-'
      return `${Number(n).toFixed(2)}%`
    },
    // "เปอร์เซนต์ของเรา" = ส่วนที่เหลือหลังยกให้เขา — คิดสดจากช่องกรอก ไม่เก็บเป็น state
    // เพราะเป็นค่าที่หักลบได้ตรง ๆ การเก็บซ้ำมีแต่จะหลุดจากกันเมื่อฝั่งใดฝั่งหนึ่งลืมอัปเดต
    // ช่องว่างคือ "ยังไม่ได้กรอก" ไม่ใช่ศูนย์ — Number('') = 0 จะกลายเป็นการตัดเอเย่นต์เหลือ 0%
    // โดยที่ทุกด่านมองว่าถูกต้อง
    isBlank(row) {
      const raw = row.editPercent
      return raw === '' || raw === null || raw === undefined
    },
    ourPercent(row) {
      if (this.isBlank(row)) return null
      const v = Number(row.editPercent)
      if (Number.isNaN(v)) return null
      return this.callerPercent - v
    },
    isRowDirty(row) {
      if (this.isBlank(row)) return true
      return Number(row.editPercent) !== Number(row.originalPercent)
    },
    // ลดต่ำกว่าที่ลูกของเขาถืออยู่ไม่ได้ — สายล่างจะได้มากกว่าที่ตัวเองได้รับ
    isBelowMin(row) {
      if (this.isBlank(row)) return false
      const v = Number(row.editPercent)
      if (Number.isNaN(v)) return false
      return v + 1e-9 < Number(row.minPercent)
    },
    // ช่วงเดียวกับที่ backend บังคับ: minPercent ≤ ที่ให้ ≤ ของเรา
    isRowValid(row) {
      if (this.isBlank(row)) return false
      const v = Number(row.editPercent)
      if (Number.isNaN(v) || v < 0) return false
      if (v > this.callerPercent + 1e-9) return false
      return !this.isBelowMin(row)
    },
    async loadHuays() {
      this.isLoadingHuays = true
      const res = await lottoService.getAuthen('huay/dict')
      if (res.status === 200) {
        this.huayOptions = (res.data || []).filter(h => h.type !== 5)
        const saved = localStorage.getItem('agent-percent-share-huay')
        if (saved) {
          this.selectedHuay = this.huayOptions.find(h => h.id === saved) || null
        }
      } else {
        this.toastError(this.$t('โหลดตลาดหวยไม่สำเร็จ'))
      }
      this.isLoadingHuays = false
      await this.loadDownline()
    },
    async onHuayChange() {
      if (this.selectedHuay) {
        localStorage.setItem('agent-percent-share-huay', this.selectedHuay.id)
      } else {
        localStorage.removeItem('agent-percent-share-huay')
      }
      await this.loadDownline()
    },
    async loadDownline() {
      this.isLoadingDownline = true
      // P2-12: guard against race when user switches huay mid-flight
      const snapshotHuayId = this.selectedHuayId
      const url = snapshotHuayId
        ? `huay-agent-percent/downline?huayId=${encodeURIComponent(snapshotHuayId)}`
        : 'huay-agent-percent/downline'
      const res = await agentService.getAuthen(url)
      // ถ้า user เปลี่ยนหวยระหว่าง await → ทิ้งผลลัพธ์นี้
      if (snapshotHuayId !== this.selectedHuayId) {
        return
      }
      if (res.status === 200) {
        const data = res.data || {}
        this.callerPercent = data.callerPercent || 0
        this.callerIsCustom = !!data.callerIsCustom
        this.rows = (data.agents || []).map(a => ({
          username: a.username,
          nickName: a.nickName,
          walletType: a.walletType,
          percent: a.percent,
          minPercent: a.minPercent,
          isCustom: a.isCustom,
          // edit state
          originalPercent: a.percent,
          editPercent: a.percent,
        }))
      } else {
        this.toastError(this.$t('โหลดข้อมูลสายล่างไม่สำเร็จ'))
      }
      this.isLoadingDownline = false
    },
    resetAllPending() {
      this.rows.forEach(r => {
        // eslint-disable-next-line no-param-reassign
        r.editPercent = r.originalPercent
      })
    },
    async savePendingChanges() {
      const dirty = this.dirtyRows.filter(r => this.isRowValid(r))
      if (dirty.length === 0) return

      const ok = await this.$swal({
        dark: true,
        title: this.$t('ยืนยันการบันทึก?'),
        text: this.$t('จะบันทึกการเปลี่ยนแปลง {count} รายการ', { count: dirty.length }),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('บันทึก'),
        cancelButtonText: this.$t('ยกเลิก'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      if (!ok.value) return

      this.isSaving = true
      const errors = []
      const isGlobalMode = !this.selectedHuayId
      const results = await Promise.all(dirty.map(row => {
        // ค่าที่กรอกคือ % ที่ยกให้เขา ตรงกับ Agent.Percent และกับช่อง "ให้เปอร์เซนต์(%)"
        // ตอนสร้างเอเย่นต์ — ส่งตรงไม่หักลบ
        const percent = Number(row.editPercent)
        const req = isGlobalMode
          ? agentService.putAuthen('huay-agent-percent/global', {
            username: row.username,
            percent,
          })
          : agentService.putAuthen('huay-agent-percent', {
            username: row.username,
            huayId: this.selectedHuayId,
            percent,
          })
        return req.then(res => ({ row, res }))
      }))
      const successCount = results.filter(({ res }) => res.status === 200).length
      results.forEach(({ row, res }) => {
        if (res.status !== 200) {
          errors.push(`${row.username}: ${typeof res.data === 'string' ? res.data : 'error'}`)
        }
      })
      this.isSaving = false

      if (successCount > 0) this.toastSuccess(this.$t('บันทึกสำเร็จ {count} รายการ', { count: successCount }))
      if (errors.length > 0) this.toastError(errors.join('\n'))
      await this.loadDownline()
    },
    async resetToGlobal(row) {
      const ok = await this.$swal({
        dark: true,
        title: this.$t('รีเซ็ตกลับค่าทุกหวย?'),
        text: this.$t('{username} จะใช้ค่าทุกหวยแทน', { username: row.username }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('รีเซ็ต'),
        cancelButtonText: this.$t('ยกเลิก'),
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!ok.value) return

      const url = `huay-agent-percent?username=${encodeURIComponent(row.username)}&huayId=${encodeURIComponent(this.selectedHuayId)}`
      const res = await agentService.deleteAuthen(url)
      if (res.status === 200) {
        this.toastSuccess(this.$t('รีเซ็ตสำเร็จ'))
        await this.loadDownline()
      } else {
        this.toastError(this.$t('รีเซ็ตไม่สำเร็จ'))
      }
    },
    openCopyModal() {
      // ปุ่ม "คัดลอกไปหวยอื่น" ระดับหน้า — default ใช้ custom row แรกถ้ามี
      const firstCustom = this.rows.find(r => r.isCustom)
      if (!firstCustom) {
        this.toastError(this.$t('ยังไม่มีเอเย่นต์ที่ตั้งค่ารายหวย — โปรดบันทึกก่อน หรือใช้ปุ่มคัดลอกในแต่ละแถว'))
        return
      }
      this.openCopyModalForRow(firstCustom)
    },
    openCopyModalForRow(row) {
      this.copyContext = {
        username: row.username,
        sourceHuayId: this.selectedHuayId,
        sourceName: this.selectedHuay.name,
        percent: row.percent,
      }
      this.copyTargetIds = []
      this.copyFilter = ''
      this.showCopyModal = true
    },
    toggleCopyTarget(id, checked) {
      if (checked) {
        if (!this.copyTargetIds.includes(id)) this.copyTargetIds.push(id)
      } else {
        this.copyTargetIds = this.copyTargetIds.filter(x => x !== id)
      }
    },
    async submitCopy() {
      if (!this.copyContext || this.copyTargetIds.length === 0) return
      const body = {
        username: this.copyContext.username,
        sourceHuayId: this.copyContext.sourceHuayId,
        targetHuayIds: this.copyTargetIds,
      }
      const res = await agentService.postAuthen('huay-agent-percent/copy-to-huays', body)
      if (res.status === 200) {
        const { copied, skipped = [], writeErrors = [] } = res.data || {}
        if (copied > 0) this.toastSuccess(this.$t('คัดลอกสำเร็จ {count} ตลาด', { count: copied }))
        if (skipped.length > 0) {
          const msg = skipped.slice(0, 3).map(s => `${s.huayId}: ${s.reason}`).join(' | ')
          this.toastError(this.$t('ข้าม {count} ตลาด: {message}', { count: skipped.length, message: msg }))
        }
        if (writeErrors.length > 0) {
          this.toastError(this.$t('บันทึกไม่สำเร็จ {count} ตลาด', { count: writeErrors.length }))
        }
        if (copied > 0 || skipped.length === 0) this.showCopyModal = false
      } else {
        this.toastError(this.$t('คัดลอกไม่สำเร็จ'))
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// ใช้ semantic highlight (warning/danger) — รองรับ dark/light ตาม theme variables อัตโนมัติ
.percent-table {
  tbody tr.row-dirty {
    background-color: var(--hdg-warning-soft);
  }

  tbody tr.row-invalid {
    background-color: var(--hdg-danger-soft);
  }

  input.is-invalid {
    border-color: var(--hdg-danger);
  }
}

.copy-huay-list {
  max-height: 320px;
  overflow-y: auto;

  .custom-checkbox {
    margin-bottom: 0.4rem;
  }
}
</style>
